// material-ui
import { alpha, createTheme } from '@mui/material/styles';

// third-party
import { presetDarkPalettes, presetPalettes, PalettesProps } from '@ant-design/colors';

// project imports
import ThemeOption from './theme';
import { ThemeMode } from 'config';

// types
import { PaletteThemeProps } from 'types/theme';
import { PresetColor } from 'types/config';

// ==============================|| DEFAULT THEME - PALETTE ||============================== //

export default function Palette(mode: ThemeMode, presetColor: PresetColor) {
  const colors: PalettesProps = mode === ThemeMode.DARK ? presetDarkPalettes : presetPalettes;

  // Paleta oficial Scale: dourado #e8ac54 (DOURADO)
  colors.gold =
    mode === ThemeMode.DARK
      ? [
          '#2A2010', // 0 - muito escuro
          '#3D2E18', // 1
          '#5C4520', // 2
          '#7A5C2A', // 3
          '#B88B3E', // 4
          '#e8ac54', // 5 - main (dourado oficial)
          '#EDBC6E', // 6
          '#F2CD8A', // 7
          '#C49448', // 8
          '#8B6B30' // 9 - escuro
        ]
      : [
          '#fbf6ed', // 0 - muito claro
          '#F7EDD9', // 1
          '#F0E0C0', // 2
          '#E8D0A0', // 3
          '#E0C080', // 4
          '#e8ac54', // 5 - main (dourado oficial)
          '#D49940', // 6
          '#B88234', // 7
          '#8B6528', // 8
          '#5C431A' // 9 - escuro
        ];

  let greyPrimary = [
    '#ffffff',
    '#fafafa',
    '#f5f5f5',
    '#f0f0f0',
    '#d9d9d9',
    '#bfbfbf',
    '#8c8c8c',
    '#595959',
    '#262626',
    '#141414',
    '#000000'
  ];
  let greyAscent = ['#fafafa', '#bfbfbf', '#434343', '#1f1f1f'];
  let greyConstant = ['#fafafb', '#e6ebf1'];

  if (mode === ThemeMode.DARK) {
    // Paleta oficial Scale: #0f0f0e (preto), #303030 (cinza médio), #ffffff
    greyPrimary = [
      '#0a0a0a', // 0 - mais escuro
      '#0f0f0e', // 1 - preto oficial Scale (fundo principal)
      '#1a1a1a', // 2 - background.paper
      '#303030', // 3 - cinza médio oficial
      '#404040',
      '#bfbfbf',
      '#d9d9d9',
      '#e0e0e0', // texto secundário
      '#f0f0f0',
      '#f7f7f7', // off-white oficial
      '#ffffff' // 10 - text.primary
    ];
    greyAscent = ['#fafafa', '#bfbfbf', '#434343', '#1f1f1f'];
    greyConstant = ['#0f0f0e', '#d3d8db'];
  }
  colors.grey = [...greyPrimary, ...greyAscent, ...greyConstant];

  const paletteColor: PaletteThemeProps = ThemeOption(colors, presetColor, mode);

  return createTheme({
    palette: {
      mode,
      common: {
        black: '#000',
        white: '#fff'
      },
      ...paletteColor,
      text: {
        // Em dark, grey[900] => último da escala (branco), mantendo boa legibilidade
        primary: mode === ThemeMode.DARK ? alpha(paletteColor.grey[900]!, 0.92) : paletteColor.grey[800],
        secondary: mode === ThemeMode.DARK ? alpha(paletteColor.grey[900]!, 0.6) : paletteColor.grey[600],
        disabled: mode === ThemeMode.DARK ? alpha(paletteColor.grey[900]!, 0.3) : paletteColor.grey[400]
      },
      action: {
        // usa a cor primária como realce global (hover/selected/focus)
        hover: alpha(paletteColor.primary.main as string, 0.08),
        selected: alpha(paletteColor.primary.main as string, 0.16),
        focus: alpha(paletteColor.primary.main as string, 0.24),
        active: alpha(paletteColor.primary.main as string, 0.9),
        disabled: paletteColor.grey[300],
        hoverOpacity: 0.08,
        selectedOpacity: 0.16,
        focusOpacity: 0.24,
        activatedOpacity: 0.12,
        disabledOpacity: 0.38
      },
      divider: mode === ThemeMode.DARK ? alpha(paletteColor.grey[900]!, 0.06) : paletteColor.grey[200],
      background: {
        // deixar o gradiente do body aparecer
        paper: mode === ThemeMode.DARK ? 'transparent' : paletteColor.grey[0],
        default: 'transparent'
      }
    }
  });
}
